import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { healthCommitteeApi } from "../../services/healthCommitteeApi";
import { employeeApi } from "../../services/employeeApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { useLocalization } from "../../hooks/useLocalization";

const STATUSES = ["OPEN", "ASSIGNED", "IN_PROGRESS", "WAITING_FOR_EVIDENCE", "COMPLETED", "OVERDUE", "CANCELLED"];

export default function HealthCommitteeActionsPage() {
  const { t } = useLocalization();
  const { canViewHealthCommittee, canManageHealthCommittee } = usePermissions();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("");
  const [completingItem, setCompletingItem] = useState(null);

  const load = useCallback(() => {
    healthCommitteeApi.listActionItems(status ? { status } : {}).then((r) => setItems(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { if (canViewHealthCommittee) { load(); employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {}); } }, [canViewHealthCommittee, load]);

  if (!canViewHealthCommittee) return <p className="text-sm text-gray-500" data-testid="health-committee-access-denied">{t("common.accessDenied")}</p>;

  const nameOf = (id) => employees.find((e) => e.id === id)?.legal_name || id;

  return (
    <div className="space-y-5" data-testid="health-committee-actions-page">
      <PageHelpBanner pageKey="health-committee-actions" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtHealthCommittee.actionsHelp") }} />
      </PageHelpBanner>
      <div>
        <Link to="/management/compliance/health-committee" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="health-committee-back-link">&larr; {t("mgmtHealthCommittee.backToDashboard")}</Link>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-1">{t("mgmtHealthCommittee.actionItems")}</h2>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="hc-actions-filter-ALL" onClick={() => setStatus("")} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${!status ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>{t("mgmtLongHourInterview.all")}</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`hc-actions-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${status === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colDescription")}</th><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colOwner")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colDue")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-gray-100" data-testid={`hc-action-board-row-${a.id}`}>
                <td className="px-4 py-2 text-gray-700">{a.description}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{a.responsible_employee_id ? nameOf(a.responsible_employee_id) : t("mgmtHealthCommittee.unassigned")}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{a.due_date || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                <td className="px-4 py-2">
                  {canManageHealthCommittee && !["COMPLETED", "CANCELLED"].includes(a.status) && (
                    <button data-testid={`hc-action-complete-btn-${a.id}`} onClick={() => setCompletingItem(a)} className="text-xs font-semibold text-emerald-600 hover:underline">{t("mgmtHealthCommittee.markCompleted")}</button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-hc-action-items">{t("mgmtHealthCommittee.noActionItemsFilter")}</td></tr>}
          </tbody>
        </table>
      </div>

      {completingItem && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setCompletingItem(null)} title={t("mgmtHealthCommittee.markActionItemCompletedTitle")}
          contextItems={[
            { label: t("mgmtHealthCommittee.colDescription"), value: completingItem.description },
            { label: t("mgmtHealthCommittee.colOwner"), value: completingItem.responsible_employee_id ? nameOf(completingItem.responsible_employee_id) : t("mgmtHealthCommittee.unassigned") },
            { label: t("mgmtHealthCommittee.dueDate"), value: completingItem.due_date || "-" },
          ]}
          reasonLabel={t("mgmtHealthCommittee.completionNote")} confirmLabel={t("mgmtHealthCommittee.markCompleted")}
          onConfirm={async (note) => { await healthCommitteeApi.updateActionItem(completingItem.id, { status: "COMPLETED", completion_note: note }); toast.success(t("mgmtHealthCommittee.actionItemCompleted")); load(); }}
          testId="hc-complete-action-dialog"
        />
      )}
    </div>
  );
}
